import React, { SetStateAction, Dispatch } from 'react';
import classnames from 'classnames';
import styles from './pagination.css';

interface PageDirectionArrowProps {
  /** Use to disable the Page Direction */
  disabled: boolean;
  /** Renders the current direction */
  direction: 'next' | 'prev';
  /** Shows the current Page Number */
  currentPage: number;
  /** Pass page change event from parent component */
  setPage: (arg: number) => void;
}

/**
 * PageDirectionArrow compnent
 *
 * @param {PageDirectionArrowProps} param
 *
 * @returns JSX.ELEMENT
 */
export const PageDirectionArrow = ({
  disabled,
  direction,
  currentPage,
  setPage
}: PageDirectionArrowProps) =>
  !disabled ? (
    <span
      className={classnames(
        styles.pagerItem,
        styles.disable,
        direction === 'next' ? styles.next : styles.prev
      )}
      title={direction === 'next' ? 'disableNext' : 'disablePrev'}
    />
  ) : (
    <a
      className={classnames(
        styles.pager,
        styles.pagerItem,
        direction === 'next' ? styles.next : styles.prev
      )}
      rel={direction}
      href="#"
      title={direction === 'next' ? 'pageNext' : 'pagePrev'}
      onClick={e => {
        e.preventDefault();
        setPage(currentPage + (direction === 'next' ? 1 : -1));
      }}
    />
  );

interface PageLinksProps {
  /** Shows the total number of pages */
  totalPages: number;
  /** Shows the current Page Number */
  currentPage: number;
  /** Pass page change event from parent component */
  setPage: (arg: number) => void | Dispatch<SetStateAction<number>>;
}

/**
 * PageLinks component
 *
 * @param {PageLinksProps} param
 *
 * @returns JSX.ELEMENT
 */
export const PageLinks = ({ totalPages, currentPage, setPage }: PageLinksProps) => {
  const PageNumberButton = (i: number) => (
    <a
      className={classnames(styles.pager, styles.pagerItem, styles.pageLink)}
      data-page={i}
      href="#"
      onClick={e => {
        e.preventDefault();
        setPage(i);
      }}
      key={i}
    >
      {i}
    </a>
  );

  const ActivePageButton = (count: number) => (
    <span className={classnames(styles.pagerItem, styles.current)} key={count}>
      {count}
    </span>
  );

  const pageItem = Array.apply(null, Array(totalPages)).map((item, i) =>
    i + 1 === currentPage ? ActivePageButton(i + 1) : PageNumberButton(i + 1)
  );

  const Gap = (listIndex: string) => (
    <span className={classnames(styles.pagerItem, styles.gap)} key={listIndex}>
      …
    </span>
  );

  if (totalPages >= 6 && totalPages < 10) {
    if (currentPage >= 5) {
      pageItem.splice(2, currentPage - 4, Gap('gap1'));
    }
  } else if (totalPages >= 10) {
    /** if there are more than 10 pages, going to replace two sets of gap dots */
    const firstGap = currentPage - 4; /** first set of gap to replace number of pages */
    const secondGapIndex = currentPage - firstGap + 2; /** second set of gap start point */
    const secondGap =
      totalPages - currentPage - 3; /** second set of gap to replace number of pages */
    if (currentPage >= 6) {
      pageItem.splice(2, firstGap, Gap('gap1'));
      if (secondGap > 0) {
        pageItem.splice(secondGapIndex, secondGap, Gap('gap2'));
      }
    } else {
      const middleGap = totalPages - currentPage - 4;
      pageItem.splice(currentPage + 1, middleGap, Gap('gap1'));
    }
  }
  return <>{pageItem}</>;
};

interface PageNumbersRangeProps {
  /** Current page number */
  currentPage: number;
  /** Total items shown per page */
  itemsPerPage: number;
  /** Total number of items */
  totalItems: number;
}

/**
 * PageNumbersRange component
 *
 * @param {PageNumbersRangeProps} param
 *
 * @returns JSX.ELEMENT
 */
export const PageNumbersRange = ({
  currentPage,
  itemsPerPage,
  totalItems
}: PageNumbersRangeProps) => {
  const currentStartPage = (currentPage - 1) * itemsPerPage + 1;
  const currentEndPage = itemsPerPage * currentPage;

  return (
    <>
      <span className={classnames(styles.pagerItem, styles.pageLead)} title="pagerRange">
        {currentStartPage} - {currentEndPage > totalItems ? totalItems : currentEndPage} of{' '}
        {totalItems}
      </span>
    </>
  );
};

interface PageButtonProps {
  /** Use to disable the Page Direction */
  disabled: boolean;
  /** Shows the total number of pages */
  totalPages: number;
  /** Shows the current direction for pagination */
  direction: 'first' | 'last';
  /** Pass page change event from parent component */
  setPage: (arg: number) => void;
}

/**
 * PageButton component
 *
 * @param {PageButtonProps} param
 *
 * @returns JSX.ELEMENT
 */
export const PageButton = ({ disabled, totalPages, direction, setPage }: PageButtonProps) =>
  !disabled ? (
    <span className={classnames(styles.pagerItem, styles.disable)}>{direction}</span>
  ) : (
    <a
      className={classnames(
        styles.pager,
        styles.pagerItem,
        direction === 'first' ? styles.first : styles.last
      )}
      rel={direction}
      href="#"
      onClick={e => {
        e.preventDefault();
        setPage(direction === 'first' ? 1 : totalPages);
      }}
    >
      {direction}
    </a>
  );
